import { cellAtWorld } from '../grid.ts';
import { idiv, imax, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, Hero, SendaNpc } from '../types.ts';
import type { EchoRules, EchoState } from '../aster-types.ts';
import { ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';

/**
 * The fog (docs/sendas/explorador.md, chapter 1, act 2, «La niebla»; §4, «El eco»; the ground in battle/aster-layout.ts).
 * Inside a Furrow in thick fog: three forks, each a chamber with three or four mouths; one goes on, the rest end two to
 * four hexes in. Sela shouts when touched (the `mark` command, 0): a ring goes out from her at `speed`, and from each
 * dead end of the fork she stands in it comes back when it has gone to its wall and back (the further, the later); from
 * the way on, nothing comes back. A shout in a dead end, or the hero at a dead end's wall, wakes a flock out of the fog.
 * The mouths are marks too (1 + its index at the fork the hero is at): the hero goes in, to the next fork or to the
 * wall. The hero sees `sight` into the fog: a tap further walks only that far that way (`echoReach`), so the board's
 * own way-finding never shows the way on. Won out of the Furrow with Sela; lost with her down or the hero.
 *
 * Cost: a shout and its echoes (a few numbers), the forks looked at once a tick; the crows hunt by the common rule.
 */

const fresh = (r: EchoRules): EchoState => ({
  fork: 0,
  shout: { x: 0, y: 0, t: -1, fork: -1 },
  echoes: [],
  heard: r.forks.map(() => 0),
  walled: r.forks.map(() => 0),
  job: -1,
  next: r.crows.first,
  shouts: 0,
  dead: 0,
  done: -1,
});

const selaOf = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'sela');

/** The fork whose chamber a point is in (−1: none, in a way between). */
const forkAt = (r: EchoRules, x: number, y: number): number => {
  for (let i = 0; i < r.forks.length; i++) {
    const f = r.forks[i] as EchoRules['forks'][number];
    if (withinRange(f.at.x, f.at.y, x, y, 1300)) return i;
  }
  return -1;
};

/** Whether a point is down a dead end (past its chamber). */
const inDeadEnd = (s: BattleState, r: EchoRules, x: number, y: number): boolean => {
  const c = cellAtWorld(s, x, y);
  for (const f of r.forks) for (const m of f.mouths) if (!m.open && m.cells.includes(c)) return true;
  return false;
};

/** A flock of soot crows out of the fog at a point. */
const flock = (s: BattleState, n: number, x: number, y: number): void => {
  for (let k = 0; k < n; k++) {
    const e = spawnEnemy(s, 'crow', 1, false, 0, x + (k % 2 ? 600 : -600), y - 400 + k * 300);
    if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  }
};

/** Sela's shout: the ring out, its echoes due from the dead ends of the fork she is in. */
const shout = (s: BattleState, r: EchoRules, w: EchoState, sela: SendaNpc, cue: (c: string) => void): void => {
  if (w.shout.t >= 0 && s.tick < w.shout.t + r.cool) return;
  const f = forkAt(r, sela.x, sela.y);
  w.shout = { x: sela.x, y: sela.y, t: s.tick, fork: f };
  w.shouts++;
  w.echoes = [];
  ev(s, { t: 'senda', kind: 'shout', npc: sela.id, x: sela.x, y: sela.y, n: f });
  cue(`shout${w.shouts}`);
  if (f >= 0) {
    const fk = r.forks[f] as EchoRules['forks'][number];
    fk.mouths.forEach((m, i) => {
      if (m.open) return;
      const dx = m.end.x - sela.x;
      const dy = m.end.y - sela.y;
      const d = isqrt(dx * dx + dy * dy);
      if (d > r.far) return;
      w.echoes.push({ m: i, t: s.tick + idiv(d * 2, r.speed) });
    });
  } else if (inDeadEnd(s, r, sela.x, sela.y)) {
    // A shout down a dead end: what sleeps in the fog there hears it.
    ev(s, { t: 'senda', kind: 'deadWall', npc: '', x: sela.x, y: sela.y, n: -1 });
    flock(s, r.crows.dead, sela.x, sela.y - 1500);
  }
};

/** The hero's taps: Sela (0) shouts; a mouth of the fork it is at (1 + its index) is walked into. */
export const echoOrder = (s: BattleState, h: Hero, at: number, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.echo;
  const w = s.senda?.echo;
  if (!r || !w || w.done >= 0) return;
  if (at === 0) {
    const sela = selaOf(s);
    if (sela && sela.hp > 0) shout(s, r, w, sela, cue);
    return;
  }
  const f = r.forks[w.fork];
  const m = f?.mouths[at - 1];
  if (!m) return;
  w.job = at - 1;
  orderMove(s, h, m.end.x, m.end.y);
  h.target = 0;
  h.forced = false;
};

/** A tap into the fog walks only as far as the hero sees that way. */
export const echoReach = (s: BattleState, at: { x: number; y: number }, x: number, y: number): { x: number; y: number } | null => {
  const r = s.rules?.senda?.echo;
  if (!r) return null;
  const dx = x - at.x;
  const dy = y - at.y;
  const d = isqrt(dx * dx + dy * dy);
  if (d <= r.sight) return null;
  return { x: at.x + idiv(dx * r.sight, d), y: at.y + idiv(dy * r.sight, d) };
};

export const echoTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.echo;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.echo ??= fresh(r));
  if (!s.started || w.done >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  const sela = selaOf(s);
  // The echoes coming back.
  if (w.echoes.length) {
    let k = 0;
    for (const e of w.echoes) {
      if (e.t > s.tick) {
        w.echoes[k++] = e;
        continue;
      }
      const f = w.shout.fork;
      const m = r.forks[f]?.mouths[e.m];
      if (!m) continue;
      const first = !w.heard.some((b) => b !== 0);
      w.heard[f] = (w.heard[f] ?? 0) | (1 << e.m);
      ev(s, { t: 'senda', kind: 'echo', npc: '', x: m.end.x, y: m.end.y, n: e.m });
      if (first) cue('heard1');
    }
    w.echoes.length = k;
  }
  // Crows out of the fog, the while: a few hexes off the hero, where it can't see.
  if (h && s.waveT >= w.next) {
    w.next = s.waveT + r.crows.every;
    const a = rngInt(s.rng, 6);
    // Just past what the eye reaches in the fog.
    const dirs = [
      [3000, 0],
      [1500, -2598],
      [-1500, -2598],
      [-3000, 0],
      [-1500, 2598],
      [1500, 2598],
    ][a] as [number, number];
    // (At Sela: she is what they go for.)
    const at = sela && sela.hp > 0 ? sela : h;
    flock(s, r.crows.n, at.x + dirs[0], at.y + dirs[1]);
  }
  if (!h) return;
  // On to a fork: the hero in its chamber.
  for (let i = w.fork + 1; i < r.forks.length; i++) {
    const f = r.forks[i] as EchoRules['forks'][number];
    if (withinRange(h.x, h.y, f.at.x, f.at.y, r.reach)) {
      w.fork = i;
      w.job = -1;
      ev(s, { t: 'senda', kind: 'fork', npc: '', x: f.at.x, y: f.at.y, n: i });
      cue(`fork${i + 1}`);
      break;
    }
  }
  // At a dead end's wall: what sleeps in the fog there wakes.
  r.forks.forEach((f, fi) => {
    f.mouths.forEach((m, mi) => {
      if (m.open || (w.walled[fi] ?? 0) & (1 << mi)) return;
      if (!withinRange(h.x, h.y, m.end.x, m.end.y, 800)) return;
      w.walled[fi] = (w.walled[fi] ?? 0) | (1 << mi);
      w.dead++;
      ev(s, { t: 'senda', kind: 'deadWall', npc: '', x: m.end.x, y: m.end.y, n: mi });
      cue(`dead${w.dead}`);
      flock(s, r.crows.dead, m.end.x, m.end.y - 1500);
    });
  });
  // Out, with Sela.
  if (withinRange(h.x, h.y, r.exit.x, r.exit.y, r.reach) && sela && sela.hp > 0 && withinRange(sela.x, sela.y, r.exit.x, r.exit.y, r.reach * 2)) {
    w.done = s.tick;
    w.fork = r.forks.length;
    ev(s, { t: 'senda', kind: 'fork', npc: '', x: r.exit.x, y: r.exit.y, n: r.forks.length });
  }
};

/** The forks reached, and out. */
export const echoGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.echo;
  const w = s.senda?.echo;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.done >= 0 ? r.forks.length + 1 : w.fork, need: r.forks.length + 1 };
};

export const echoResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = s.senda?.echo;
  if (!w || w.done < 0) return null;
  return s.tick >= w.done + 6 ? 'won' : null;
};
